import React, { useState } from 'react';
import _t from 'counterpart';
import { useParams } from 'react-router';
import { useMutation, useQuery } from 'react-query';
import { cancelOrder, loadOrderQuery } from '../../services/BackendService';
import { extractErrorMessage, formatCurrency, formatDateTime, handleFormError } from '../../helpers';
import { CRow, CCol, CCard, CCardBody, CLink, CButton, CBadge } from '@coreui/react';
import Loading from '../../components/Loading';
import OrderStatusBadge from './OrderStatusBadge';
import PageLayout from '../../components/PageLayout';
import Error from '../../components/Error';
import { Permission } from '../../reducers/userReducer';
import { useAppSelector } from '../../helpers/customHooks';
import { IOrder, StatusType } from './types';
import { WalletPlatform } from '../wallets/types';
import PlatformBadge from '../../components/PlatformBadge';
import OrderUpdateModal from './OrderUpdateModal';
import TradingSideBadge from '../../components/TradingSideBadge';
import ConfirmationModal from '../../components/ConfirmationModal';
import toast from 'react-hot-toast';

const OrderPage = () => {
  const [editOrderShow, setEditOrderShow] = useState<IOrder | null>(null);
  const [toCancelOrder, setToCancelOrder] = useState<boolean>(false);

  const { id } = useParams() as any;

  const permissions = useAppSelector((state) => state.user.permissions);

  const orderQuery = useQuery(['order', id], () => loadOrderQuery(id || ''), {
    onError: (e: any) => {
      toast.error(extractErrorMessage(e));
    },
    retry: false,
  });

  const cancelOrderMutation = useMutation((id: string) => cancelOrder(id), {
    onSuccess: async () => {
      toast.success(_t('orders.order-canceled-successfully'));
    },
    onError: (e: any) => {
      handleFormError(e);
    },
    retry: false,
  });

  const onErrorRetry = () => {
    orderQuery.refetch();
  };

  if (orderQuery.isLoading || orderQuery.isIdle) {
    return <Loading />;
  }

  if (orderQuery.isError) {
    return <Error onRetry={onErrorRetry} />;
  }

  const order = orderQuery.data;

  // MT5 orders are read-only here: editing or cancelling one means asking MT5, and these actions
  // speak to the Wintrado gateway, which holds no such order.
  const showActions =
    order.platform !== WalletPlatform.MT5 &&
    ['new', 'accepted', 'pending'].includes(orderQuery.data.status) &&
    permissions.includes(Permission.MANAGE_ORDERS);

  const onEditClicked = () => {
    setEditOrderShow(orderQuery.data);
  };

  const onEditClosed = (refetch?: boolean) => {
    setEditOrderShow(null);
    if (refetch) {
      orderQuery.refetch();
    }
  };

  const cancelOrderToggle = () => {
    setToCancelOrder(true);
  };

  const onCanceledOrder = () => {
    setToCancelOrder(false);
  };

  const callCancelOrderMutation = () => {
    setToCancelOrder(false);
    cancelOrderMutation.mutate(order.id.toString());
  };

  return (
    <PageLayout
      title={_t.translate('orders.order.title') + ` #${order.id}`}
      titleAppend={
        <div>
          {showActions && (
            <div className="d-flex justify-content-between float-right mb-2">
              <div className="d-flex flex-nowrap flex-row-reverse">
                <CButton color="primary" onClick={onEditClicked}>
                  {_t('action.edit')}
                </CButton>
                {permissions?.includes(Permission.MANAGE_ORDERS) && (
                  <>
                    {(order.status === StatusType.pending || order.status === StatusType.accepted) && (
                      <CButton color="danger" className="mr-2" onClick={() => cancelOrderToggle()}>
                        {_t('orders.cancel-order')}
                      </CButton>
                    )}
                  </>
                )}
              </div>
            </div>
          )}
        </div>
      }
    >
      <CRow>
        <CCol md={12}>
          <CCard>
            <CCardBody>
              <CRow>
                <CCol md={6}>
                  <dl>
                    <dt>{_t('positions.platform')}:</dt>
                    <dd>
                      <PlatformBadge platform={order.platform} />
                    </dd>
                  </dl>

                  <dl>
                    <dt>{_t('orders.position-id')}:</dt>
                    <dd>
                      {/* The platform's own id is what gets shown; the link needs the CRM's, which
                          an order does not always have — a pending order has no position yet. */}
                      {order.positionId ? (
                        <CLink href={`/positions/${order.positionId}`}>
                          {order.positionExternalId ?? order.positionId}
                        </CLink>
                      ) : (
                        order.positionExternalId ?? '-'
                      )}
                    </dd>
                  </dl>

                  <dl>
                    <dt>{_t('orders.placedAt')}:</dt>
                    <dd>{order.placedAt ? formatDateTime(order.placedAt) : '-'}</dd>
                  </dl>

                  <dl>
                    <dt>{_t('orders.request-price')}:</dt>
                    <dd>{order.requestPrice || '-'}</dd>
                  </dl>

                  <dl>
                    <dt>{_t('orders.quantity')}:</dt>
                    <dd>
                      {order.quantity} / {order.filledQuantity ?? '-'}
                    </dd>
                  </dl>

                  <dl>
                    <dt>{_t('orders.notional-value')}:</dt>
                    <dd>{order.notionalValue || '-'}</dd>
                  </dl>

                  <dl>
                    <dt>{_t('global.status')}:</dt>
                    <dd>
                      <OrderStatusBadge status={order.status} />
                    </dd>
                  </dl>

                  <dl>
                    <dt>{_t('orders.side')}:</dt>
                    <dd>
                      <TradingSideBadge side={order.side} />
                    </dd>
                  </dl>

                  <dl>
                    <dt>{_t('orders.stop-loss')}</dt>
                    <dd>{order.stopLoss ? order.stopLoss : '-'}</dd>
                  </dl>

                  <dl>
                    <dt>{_t('orders.trailing-stop-loss-pct')}</dt>
                    <dd>{order.trailingStopLossPct ? order.trailingStopLossPct + '%' : '-'}</dd>
                  </dl>

                  <dl>
                    <dt>{_t('orders.take-profit')}</dt>
                    <dd>{order.takeProfit ? order.takeProfit : '-'}</dd>
                  </dl>
                </CCol>
                <CCol md={6}>
                  <dl>
                    <dt>{_t('orders.external-id')}:</dt>
                    <dd>{order.externalId}</dd>
                  </dl>

                  <dl>
                    <dt>{_t('orders.executed-at')}:</dt>
                    <dd>{order.executedAt ? formatDateTime(order.executedAt) : '-'}</dd>
                  </dl>

                  <dl>
                    <dt>{_t('orders.average-price')}:</dt>
                    <dd>{order.averagePrice || '-'}</dd>
                  </dl>

                  <dl>
                    <dt>{_t('orders.notional-value-in-usd')}:</dt>
                    <dd>{order.notionalValueInUSD ? formatCurrency(order.notionalValueInUSD) : '-'}</dd>
                  </dl>

                  <dl>
                    <dt>{_t('orders.reject-reason')}:</dt>
                    <dd>{order.rejectReason || '-'}</dd>
                  </dl>

                  <dl>
                    <dt>{_t('global.type')}:</dt>
                    <dd>
                      <CBadge color={order.type === 'market' ? 'success' : 'primary'}>{order.type}</CBadge>
                    </dd>
                  </dl>

                  <dl>
                    <dt>{_t('orders.stop-price')}</dt>
                    <dd>{order.stopPrice ? order.stopPrice : '-'}</dd>
                  </dl>

                  <dl>
                    <dt>{_t('orders.limit-price')}</dt>
                    <dd>{order.limitPrice ? order.limitPrice : '-'}</dd>
                  </dl>

                  <dl>
                    <dt>{_t('orders.user-ip-address')}</dt>
                    <dd>{order.userIpAddress ? order.userIpAddress : '-'}</dd>
                  </dl>

                  <dl>
                    <dt>{_t('orders.trading-adapter')}</dt>
                    <dd>
                      {order.tradingAdapter ? (
                        <CLink href={`/adapters/${order.tradingAdapter}`}>{order.tradingAdapter}</CLink>
                      ) : (
                        '-'
                      )}
                    </dd>
                  </dl>
                </CCol>
              </CRow>
            </CCardBody>
          </CCard>
        </CCol>
      </CRow>
      <ConfirmationModal
        id={order.id!.toString()}
        show={toCancelOrder}
        hidePanel={onCanceledOrder}
        onConfirm={callCancelOrderMutation}
        title={_t('orders.cancel-order')}
        error=""
        buttonColor="danger"
        withMutation={false}
      />
      <OrderUpdateModal order={editOrderShow} onClose={onEditClosed} />
    </PageLayout>
  );
};

export default OrderPage;
